import { useState, useMemo, useCallback, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { httpClient } from '../api/http';
import { WithdrawalManagersParams, WithrawalDataType } from '../types';
import { queryParamBuilder } from '../helpers/utils';

const DEFAULT_LIMIT = 20;

const defaultParams: WithdrawalManagersParams = {
  limit: DEFAULT_LIMIT,
  cursorId: undefined,
  query: '',
  queryFilter: '',
  startDate: '',
  endDate: '',
  statusFilter: '',
  modeFilter: '',
};

const useWithdrawalManager = (context: 'normalWithdrawal' | 'largeWithdrawal') => {
  const [withdrawalManagerParams, setWithdrawalManagerParams] = useState<WithdrawalManagersParams>({
    ...defaultParams,
  });

  const [page, setPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const cursorHistory = useRef<(number | undefined)[]>([undefined]);
  const [withdrawalLimit, setWithdrawalLimit] = useState<{ pocket: number; bank: number }>({
    pocket: 3000000,
    bank: 5100000,
  });

  const canResetSearch = useMemo(() => {
    return withdrawalManagerParams.query !== '' && withdrawalManagerParams.queryFilter !== '';
  }, [withdrawalManagerParams]);

  const canResetFilter = useMemo(() => {
    return (
      (withdrawalManagerParams.startDate !== '' ||
        withdrawalManagerParams.endDate !== '' ||
        withdrawalManagerParams.statusFilter !== '' ||
        withdrawalManagerParams.modeFilter !== '') &&
      !canResetSearch
    );
  }, [withdrawalManagerParams, canResetSearch]);

  const buildApiParams = () => {
    const params: Record<string, any> = {
      cursorId: withdrawalManagerParams.cursorId,
      limit: withdrawalManagerParams.limit || DEFAULT_LIMIT,
    };

    if (withdrawalManagerParams.queryFilter && withdrawalManagerParams.query) {
      params[withdrawalManagerParams.queryFilter] = withdrawalManagerParams.query;
    }

    if (withdrawalManagerParams.statusFilter) params.statusFilter = withdrawalManagerParams.statusFilter;
    if (withdrawalManagerParams.modeFilter) params.modeFilter = withdrawalManagerParams.modeFilter;
    if (withdrawalManagerParams.startDate) params.startDate = withdrawalManagerParams.startDate;
    if (withdrawalManagerParams.endDate) params.endDate = withdrawalManagerParams.endDate;
    if (withdrawalManagerParams.destination) params.destination = withdrawalManagerParams.destination;

    return params;
  };

  const queryKey = [`withdrawal-${context}`, withdrawalManagerParams];

  const {
    data: withdrawalsList,
    isLoading,
    error,
    refetch,
    isFetching,
  } = useQuery<WithrawalDataType[]>({
    queryKey,
    queryFn: async () => {
      const apiParams = buildApiParams();
      const endpoint = context === 'normalWithdrawal' ? 'fetch-all' : 'fetch-onhold';
      const url = `admin/withdrawal-manager/${endpoint}${queryParamBuilder(apiParams)}`;
      const response = await httpClient.get(url);

      if (!response.data.status) throw new Error('Fetch failed');

      const { list, pageInfo, withdrawalLimit } = response.data.data;

      setWithdrawalLimit(withdrawalLimit);
      setHasNextPage(pageInfo?.nextPage ?? false);

      if (pageInfo?.nextPage && pageInfo?.cursorId && cursorHistory.current.length <= page) {
        cursorHistory.current.push(pageInfo.cursorId);
      }

      return list ?? [];
    },
    refetchOnWindowFocus: true,
  });

  const goToNextPage = useCallback(() => {
    if (!hasNextPage) return;
    const nextPage = page + 1;
    const nextCursor = cursorHistory.current[nextPage - 1];
    setPage(nextPage);
    setWithdrawalManagerParams((prev) => ({
      ...prev,
      cursorId: nextCursor ? Number(nextCursor) : undefined,
    }));
  }, [hasNextPage, page]);

  const goToPrevPage = useCallback(() => {
    if (page <= 1) return;
    const prevPage = page - 1;
    const prevCursor = cursorHistory.current[prevPage - 1];
    setPage(prevPage);
    setWithdrawalManagerParams((prev) => ({
      ...prev,
      cursorId: prevCursor ? Number(prevCursor) : undefined,
    }));
  }, [page]);

  const setLimit = useCallback((limit: number) => {
    cursorHistory.current = [undefined];
    setPage(1);
    setWithdrawalManagerParams((prev) => ({
      ...prev,
      limit,
      cursorId: undefined,
    }));
  }, []);

  const fetchData = useCallback((params?: WithdrawalManagersParams) => {
    cursorHistory.current = [undefined];
    setPage(1);
    setHasNextPage(false);
    if (!params) {
      setWithdrawalManagerParams({ ...defaultParams });
      return;
    }
    setWithdrawalManagerParams((prev) => ({ ...prev, ...params, cursorId: undefined }));
  }, []);

  return {
    isLoading: isLoading || isFetching,
    withdrawalsList: withdrawalsList ?? [],
    pagination: {
      page,
      pageSize: withdrawalManagerParams.limit || DEFAULT_LIMIT,
      hasNextPage,
      hasPrevPage: page > 1,
    },
    refetch,
    error,
    withdrawalManagerParams,
    setWithdrawalManagerParams,
    canResetSearch,
    canResetFilter,
    fetchData,
    goToNextPage,
    goToPrevPage,
    setLimit,
    withdrawalLimit,
  };
};

export default useWithdrawalManager;
